import React, { Fragment } from "react";
import { RadioGroup } from "@headlessui/react";

import { Label } from "@helloaudio/ui";
import { cn } from "@helloaudio/utils";

interface InlineRadioGroupProps<T> {
  alwaysStackOptions?: boolean;
  checkedOptionsClassName?: string;
  className?: string;
  disabled?: boolean;
  grow?: boolean;
  label?: string;
  labelClassName?: string;
  name?: string;
  onChange: (value: T) => void;
  options: {
    key?: string;
    label: React.ReactNode;
    value: T;
    disabled?: boolean;
  }[];
  optionsClassName?: string;
  value: T;
}
function InlineRadioGroup<T>(props: InlineRadioGroupProps<T>) {
  const {
    alwaysStackOptions,
    checkedOptionsClassName,
    className,
    disabled,
    grow,
    label,
    labelClassName,
    onChange,
    options,
    optionsClassName,
    value,
  } = props;

  return (
    <RadioGroup className={className} value={value} onChange={onChange}>
      {label && (
        <RadioGroup.Label
          as={Label}
          className={cn("tw-mb-2 tw-text-sm tw-font-Montserrat", labelClassName)}
        >
          {label}
        </RadioGroup.Label>
      )}
      <fieldset
        className={cn("tw-flex tw-flex-col tw-flex-wrap tw-gap-2", {
          "md:tw-flex-row md:tw-gap-4 md:tw-items-center": !alwaysStackOptions,
        })}
      >
        <legend className="tw-sr-only">Available options</legend>
        {options.map((option, index) => (
          <RadioGroup.Option
            key={option.key !== undefined ? option.key : index}
            value={option.value}
            disabled={disabled || option.disabled}
            className={({ checked, active, disabled }) =>
              cn(
                "tw-flex tw-items-center tw-h-9 tw-pl-3 tw-pr-3 tw-border tw-border-solid tw-rounded tw-font-Montserrat focus:tw-outline-none tw-w-fit",
                grow && "tw-grow",
                checked && "tw-border-green-500",
                !checked && "tw-border-gray-300",
                active && "tw-ring-2 tw-ring-green-300",
                !disabled && "tw-cursor-pointer",
                disabled && "tw-bg-gray-100 tw-cursor-not-allowed",
                optionsClassName,
                checked && checkedOptionsClassName,
              )
            }
          >
            {({ checked, disabled }) => (
              <Fragment>
                <div
                  className={cn(
                    "tw-w-5 tw-h-5 tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-border tw-border-solid tw-rounded-full",
                    checked ? "tw-border-green-500" : "tw-border-gray-300",
                  )}
                >
                  {checked && <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-green-500" />}
                </div>
                <label
                  className={cn(
                    "tw-ml-2 tw-mb-0 tw-block tw-text-sm tw-leading-4 tw-font-medium",
                    !disabled && "tw-text-black tw-cursor-pointer",
                    disabled && "tw-text-gray-500 tw-cursor-not-allowed",
                  )}
                >
                  {option.label}
                </label>
              </Fragment>
            )}
          </RadioGroup.Option>
        ))}
      </fieldset>
    </RadioGroup>
  );
}

export default InlineRadioGroup;
